import { Box, Button, IconButton, SvgIcon, Toolbar, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { palette, spaces } from '../../assets/styles/themes/tokens';
import formatByteSize from '../../utils/formatByteSize';
import getMuiIcon from '../../utils/getMuiIcon';

interface AttachmentsTableToolbarProps {
	numSelected: number;
	selectedFilesSize: number; // In bytes
	onClickBackButton?: () => void;
	subFolderTitle?: string;
	onClickDownload: () => Promise<void>;
}

const AttachmentsTableToolbar = ({
	numSelected,
	selectedFilesSize,
	onClickBackButton,
	subFolderTitle,
	onClickDownload,
}: AttachmentsTableToolbarProps) => {
	const { t } = useTranslation();
	const [isDownloadButtonDisabled, setIsDownloadButtonDisabled] = useState<boolean>(false);

	const onClickDownloadButton = async () => {
		setIsDownloadButtonDisabled(true);
		await onClickDownload();
		setIsDownloadButtonDisabled(false);
	};

	return (
		<Toolbar
			data-testid="attachment-table-toolbar"
			sx={{
				padding: `${spaces.xxs} ${spaces.xxs} ${spaces.xxs} ${onClickBackButton ? spaces.sm : spaces.md} !important`,
				borderRadius: spaces.md,
				backgroundColor: palette.background.paperElevation2,
			}}
		>
			{numSelected > 0 ? (
				<Box sx={{ display: 'flex', alignItems: 'center', flex: '1 1 100%' }}>
					<Typography sx={{ marginRight: spaces.xs, color: palette.text.primary }} variant="h6">
						{numSelected} {t('selected')}
					</Typography>
					<Typography variant="caption" sx={{ color: palette.text.primary }}>
						{` • ${formatByteSize(selectedFilesSize)}`}
					</Typography>
				</Box>
			) : (
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						flex: '1 1 100%',
					}}
				>
					{onClickBackButton && (
						<IconButton
							data-testid="attachment-toolbar-back-button"
							size="small"
							onClick={onClickBackButton}
							sx={{ marginRight: spaces.md }}
						>
							<SvgIcon
								sx={{
									width: 24,
									height: 24,
									color: palette.text.primary,
								}}
								component={getMuiIcon('arrowLeft')}
							/>
						</IconButton>
					)}
					<Typography
						variant="h6"
						id="tableTitle"
						sx={{
							color: palette.text.primary,
						}}
					>
						{subFolderTitle || t('Attachments')}
					</Typography>
				</Box>
			)}
			<Button
				data-testid="attachment-toolbar-download-button"
				variant="text"
				size="large"
				sx={{
					padding: `${spaces.xs} ${spaces.lg}`,
					color: numSelected === 0 ? palette.action.disabled : palette.primary.main,
				}}
				onClick={onClickDownloadButton}
				disabled={numSelected === 0 || isDownloadButtonDisabled}
				startIcon={
					<SvgIcon
						component={getMuiIcon('downloadFileIcon')}
						sx={{
							fill: numSelected === 0 ? palette.action.disabled : palette.primary.main,
						}}
					/>
				}
			>
				{t('Download')}
			</Button>
		</Toolbar>
	);
};

export default AttachmentsTableToolbar;
